import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { SlidersHorizontal, X } from "lucide-react";
import api, { asArray } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [priceRange, setPriceRange] = useState([0, 20000]);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const category = params.get("category") || "";
  const q = params.get("q") || "";
  const sort = params.get("sort") || "newest";
  const availability = params.get("availability") || "";
  const tag = params.get("tag") || "";
  const min_price = params.get("min_price");
  const max_price = params.get("max_price");

  useEffect(() => { api.get("/categories").then((r) => setCategories(asArray(r.data))).catch(() => {}); }, []);

  useEffect(() => {
    const qs = new URLSearchParams();
    if (category) qs.set("category", category);
    if (q) qs.set("q", q);
    if (sort) qs.set("sort", sort);
    if (availability) qs.set("availability", availability);
    if (tag) qs.set("tag", tag);
    if (min_price) qs.set("min_price", min_price);
    if (max_price) qs.set("max_price", max_price);
    setLoading(true);
    api.get(`/products?${qs}`).then((r) => setProducts(asArray(r.data))).finally(() => setLoading(false));
  }, [category, q, sort, availability, tag, min_price, max_price]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (!v) next.delete(k);
    else next.set(k, v);
    setParams(next);
  };

  const applyPrice = () => {
    const next = new URLSearchParams(params);
    next.set("min_price", String(priceRange[0]));
    next.set("max_price", String(priceRange[1]));
    setParams(next);
  };

  const clearAll = () => setParams(new URLSearchParams(q ? { q } : {}));

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16" data-testid="shop-page">
      <div className="pb-10 border-b divider-gold">
        <div className="overline text-emerald mb-3">The shop</div>
        <h1 className="font-serif text-5xl md:text-6xl tracking-tight">{q ? `Results for "${q}"` : "All objects"}</h1>
        <p className="mt-4 text-charcoal/60 max-w-xl text-[15px]">
          Every piece here is made in small numbers. When it's gone, it's gone — replaced by something new, but never the same.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-12 lg:gap-16 pt-12">
        {/* Sidebar */}
        <aside className={`${filtersOpen ? "block" : "hidden lg:block"}`}>
          <div className="flex items-center justify-between lg:justify-start gap-3 mb-6">
            <div className="overline text-charcoal">Filters</div>
            <button onClick={clearAll} className="text-[11px] tracking-widest uppercase text-charcoal/50 hover:text-emerald">Clear all</button>
          </div>

          <div className="space-y-8">
            <div>
              <div className="overline text-charcoal/70 mb-4">Category</div>
              <div className="space-y-2 text-sm">
                {[{ slug: "", name: "All" }, ...categories].map((c) => (
                  <button
                    key={c.slug || "all"}
                    data-testid={`filter-cat-${c.slug || "all"}`}
                    onClick={() => setParam("category", c.slug)}
                    className={`block w-full text-left py-1 ${category === c.slug ? "text-emerald" : "text-charcoal/70 hover:text-charcoal"}`}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="overline text-charcoal/70 mb-4">Price</div>
              <Slider value={priceRange} onValueChange={setPriceRange} max={20000} step={500} className="mb-3" />
              <div className="text-xs text-charcoal/60 flex justify-between">
                <span>₹{priceRange[0]}</span><span>₹{priceRange[1]}</span>
              </div>
              <Button data-testid="apply-price" onClick={applyPrice} variant="ghost" className="mt-3 h-8 px-0 overline text-emerald hover:bg-transparent">Apply →</Button>
            </div>

            <div>
              <div className="overline text-charcoal/70 mb-4">Availability</div>
              <label className="flex items-center gap-3 text-sm cursor-pointer">
                <Checkbox checked={availability === "in-stock"} onCheckedChange={(v) => setParam("availability", v ? "in-stock" : "")} data-testid="filter-instock" />
                In stock only
              </label>
            </div>

            <div>
              <div className="overline text-charcoal/70 mb-4">Highlights</div>
              <div className="space-y-2 text-sm">
                {[{ key: "", label: "All" }, { key: "new-arrival", label: "New arrivals" }, { key: "best-seller", label: "Best sellers" }, { key: "featured", label: "Featured" }].map((t) => (
                  <button key={t.key || "all-tag"} onClick={() => setParam("tag", t.key)} className={`block w-full text-left py-1 ${tag === t.key ? "text-emerald" : "text-charcoal/70 hover:text-charcoal"}`}>{t.label}</button>
                ))}
              </div>
            </div>
          </div>
        </aside>

        {/* Products */}
        <div>
          <div className="flex items-center justify-between mb-8">
            <button onClick={() => setFiltersOpen((v) => !v)} className="lg:hidden inline-flex items-center gap-2 text-sm">
              {filtersOpen ? <X className="w-4 h-4" /> : <SlidersHorizontal className="w-4 h-4" />}
              Filters
            </button>
            <div className="text-xs text-charcoal/60">{products.length} object{products.length === 1 ? "" : "s"}</div>
            <Select value={sort} onValueChange={(v) => setParam("sort", v)}>
              <SelectTrigger data-testid="sort-trigger" className="w-[200px] border-0 border-b divider-gold rounded-none bg-transparent focus:ring-0 text-sm h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest first</SelectItem>
                <SelectItem value="price-asc">Price · low to high</SelectItem>
                <SelectItem value="price-desc">Price · high to low</SelectItem>
                <SelectItem value="popularity">Most loved</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-12">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[3/4] bg-ivory" />
                  <div className="h-4 mt-4 bg-ivory w-2/3" />
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="py-32 text-center text-charcoal/50 font-serif text-2xl">Nothing here yet — try adjusting the filters.</div>
          ) : (
            <motion.div layout className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-12">
              {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
